import React, { useState, useEffect } from 'react';
import {
    CreditCard, CheckCircle, AlertTriangle, QrCode, Loader2, Copy,
    ArrowUpCircle, Building, CalendarClock, History, Shield,
    MapPin, Phone, Mail, FileText,
    DollarSign, Check, XCircle, PlusCircle, Sparkles
} from 'lucide-react';

// --- SERVIÇOS LOCAIS ---
import api from '../services/api';
import { socket } from '../services/socket';
import Modal from '../components/Modal';

// Calcula pró-rata localmente para preview no modal
const calcLocalProRata = (nextDueDate, diffUsers) => {
    const pricePerExtra = 99.90;
    const now = new Date();
    const due = nextDueDate ? new Date(nextDueDate) : (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d; })();
    const msRemaining = due - now;
    const daysRemaining = Math.max(1, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
    const factor = Math.min(1, daysRemaining / 30);
    const proRataAmount = Math.max(1.00, parseFloat((diffUsers * pricePerExtra * factor).toFixed(2)));
    const fullMonthly = diffUsers * pricePerExtra;
    return { daysRemaining, factor, proRataAmount, fullMonthly };
};

const AssinaturaScreen = ({ currentUser }) => {
    // --- ESTADOS ---
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    // Preços dos Módulos Adicionais (vindos do SuperAdmin / SystemSettings)
    const [modulePrices, setModulePrices] = useState({ nfse: 49.90, boletos: 49.90 });

    // Dados de Pagamento (Pix)
    const [paymentData, setPaymentData] = useState(null); // { qrCodeBase64, copyPaste }

    // Modais
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [desiredUsers, setDesiredUsers] = useState(3);

    // Modal de Contratação de Módulo
    const [isHireModalOpen, setIsHireModalOpen] = useState(false);
    const [hiringModule, setHiringModule] = useState(null); // { key, name, price, description }
    const [hireProRata, setHireProRata] = useState(null);

    // ==================================================================================
    // 1. INICIALIZAÇÃO E SOCKET
    // ==================================================================================
    useEffect(() => {
        fetchCompanyData();

        // Escuta confirmação de pagamento em tempo real
        socket.on('payment_confirmed', () => {
            alert("Pagamento confirmado! Sua assinatura está ativa.");
            setPaymentData(null); // Fecha o QR Code
            fetchCompanyData(); // Recarrega dados
        });

        // Escuta mudanças na empresa/módulos
        socket.on('company_updated', (data) => {
            setCompany(prev => ({ ...prev, ...data }));
        });

        return () => {
            socket.off('payment_confirmed');
            socket.off('company_updated');
        };
    }, []);

    const fetchCompanyData = async () => {
        setLoading(true);
        try {
            const [resComp, resSettings] = await Promise.all([
                api.get('/company/status'),
                api.get('/system/settings').catch(() => ({ data: {} }))
            ]);
            setCompany(resComp.data);
            setDesiredUsers(resComp.data.usersLimit || 3);
            if (resSettings.data) {
                setModulePrices({
                    nfse: resSettings.data.modulePriceNfse ?? 49.90,
                    boletos: resSettings.data.modulePriceBoletos ?? 49.90
                });
            }
        } catch (error) {
            console.error("Erro ao carregar assinatura:", error);
        } finally {
            setLoading(false);
        }
    };

    // ==================================================================================
    // 2. AÇÕES FINANCEIRAS
    // ==================================================================================

    // Gerar Pix (Renovação da Mensalidade Completa)
    const handleGeneratePix = async () => {
        setProcessing(true);
        try {
            const users = company.usersLimit || 3;
            const baseAmount = 199.90 + (Math.max(0, users - 3) * 99.90);
            const nfseAmount = company.modules?.notas_fiscais ? (modulePrices.nfse || 49.90) : 0;
            const boletosAmount = company.modules?.financeiro ? (modulePrices.boletos || 49.90) : 0;
            const totalAmount = baseAmount + nfseAmount + boletosAmount;

            const res = await api.post('/subscription/pay', {
                amount: totalAmount.toFixed(2),
                description: `Mensalidade WA-Desk - ${users} usuários${company.modules?.notas_fiscais ? ' + NFS-e' : ''}${company.modules?.financeiro ? ' + Boletos' : ''}`
            });

            setPaymentData(res.data);
        } catch (error) {
            console.error(error);
            alert("Erro ao gerar Pix. Verifique se o módulo de pagamento está configurado no servidor.");
        } finally {
            setProcessing(false);
        }
    };

    // Confirmar mudança de plano (usuários)
    const handleUpgradeConfirm = async () => {
        setIsUpgradeModalOpen(false);
        setProcessing(true);
        try {
            const res = await api.post('/subscription/upgrade', { newLimit: desiredUsers });

            if (res.data.paymentRequired) {
                setPaymentData(res.data.paymentData);
            } else {
                alert("Plano alterado com sucesso!");
                fetchCompanyData();
            }
        } catch (error) {
            alert("Erro ao alterar plano.");
        } finally {
            setProcessing(false);
        }
    };

    // Abrir modal de contratação de módulo com cálculo de pró-rata
    const handleOpenHireModal = (moduleKey, moduleName, modulePrice, description) => {
        const now = new Date();
        const due = company?.next_due_date ? new Date(company.next_due_date) : (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d; })();
        const msRemaining = due - now;
        const daysRemaining = Math.max(1, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
        const factor = Math.min(1, daysRemaining / 30);
        const proRataAmount = Math.max(1.00, parseFloat((modulePrice * factor).toFixed(2)));

        setHireProRata({ daysRemaining, factor, proRataAmount, fullMonthly: modulePrice });
        setHiringModule({ key: moduleKey, name: moduleName, price: modulePrice, description });
        setIsHireModalOpen(true);
    };

    // Confirmar contratação de módulo
    const handleConfirmHireModule = async () => {
        if (!hiringModule) return;
        setIsHireModalOpen(false);
        setProcessing(true);
        try {
            const res = await api.post('/subscription/module/hire', { moduleKey: hiringModule.key });
            if (res.data.paymentRequired) {
                setPaymentData(res.data.paymentData);
            } else {
                alert(res.data.message || `Módulo ${hiringModule.name} contratado com sucesso!`);
                fetchCompanyData();
            }
        } catch (error) {
            alert("Erro ao contratar módulo: " + (error.response?.data?.error || error.message));
        } finally {
            setProcessing(false);
        }
    };

    // Cancelar módulo contratado
    const handleCancelModule = async (moduleKey, moduleName) => {
        if (!window.confirm(`Deseja realmente desativar o módulo "${moduleName}"? As funcionalidades vinculadas não estarão mais disponíveis para a sua empresa.`)) {
            return;
        }
        setProcessing(true);
        try {
            const res = await api.post('/subscription/module/cancel', { moduleKey });
            alert(res.data.message || `Módulo ${moduleName} desativado com sucesso.`);
            fetchCompanyData();
        } catch (error) {
            alert("Erro ao desativar módulo: " + (error.response?.data?.error || error.message));
        } finally {
            setProcessing(false);
        }
    };

    const copyToClipboard = () => {
        if (paymentData?.copyPaste) {
            navigator.clipboard.writeText(paymentData.copyPaste);
            alert("Código Pix copiado!");
        }
    };

    // ==================================================================================
    // 3. RENDERIZAÇÃO
    // ==================================================================================

    if (loading) return <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin text-blue-600" /></div>;
    if (!company) return <div className="p-8 text-center text-gray-500">Erro ao carregar dados da empresa.</div>;

    const isOverdue = company.status === 'overdue' || company.status === 'suspended';
    
    // Cálculos de Preço
    const baseUsers = 3;
    const basePrice = 199.90;
    const extraPrice = 99.90;
    const extraUsers = Math.max(0, (company.usersLimit || 3) - baseUsers);
    const usersTotal = basePrice + (extraUsers * extraPrice);

    const isNfseActive = !!company.modules?.notas_fiscais;
    const isBoletosActive = !!company.modules?.financeiro;
    const priceNfse = modulePrices.nfse ?? 49.90;
    const priceBoletos = modulePrices.boletos ?? 49.90;

    const currentPrice = usersTotal + (isNfseActive ? priceNfse : 0) + (isBoletosActive ? priceBoletos : 0);

    return (
        <div className="p-6 h-full bg-gray-50 overflow-y-auto font-sans">
            <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <CreditCard className="mr-3 text-blue-600" /> Assinatura e Planos
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* COLUNA ESQUERDA: STATUS, PAGAMENTO, MÓDULOS E DADOS */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Card de Status */}
                    <div className={`p-6 rounded-xl border shadow-sm relative overflow-hidden flex flex-col md:flex-row justify-between items-center
                        ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}
                    `}>
                        <div className="z-10 mb-4 md:mb-0">
                            <h2 className={`text-lg font-bold flex items-center ${isOverdue ? 'text-red-800' : 'text-gray-800'}`}>
                                {isOverdue ? <AlertTriangle className="mr-2" /> : <CheckCircle className="mr-2 text-green-500" />}
                                Status: {isOverdue ? 'Pagamento Pendente' : 'Assinatura Ativa'}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {isOverdue
                                    ? 'Regularize para evitar o bloqueio do sistema.'
                                    : `Próxima renovação em: ${company.next_due_date ? new Date(company.next_due_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Data não definida'}`
                                }
                            </p>
                        </div>

                        {!paymentData && (
                            <button
                                onClick={handleGeneratePix}
                                disabled={processing}
                                className={`z-10 px-6 py-3 rounded-lg font-bold shadow-lg flex items-center ${isOverdue ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' : 'bg-green-600 text-white hover:bg-green-700'}`}
                            >
                                {processing ? <Loader2 className="animate-spin mr-2" /> : <QrCode className="mr-2" />}
                                {isOverdue ? 'Pagar Pendência' : 'Pagar Assinatura'}
                            </button>
                        )}
                    </div>

                    {/* Área do PIX (Só aparece se tiver gerado) */}
                    {paymentData && (
                        <div className="bg-white p-6 rounded-xl border-2 border-blue-500 shadow-lg animate-in fade-in slide-in-from-top-4">
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-bold text-gray-800">Pagamento via Pix</h3>
                                <p className="text-gray-500">Escaneie o QR Code ou copie o código abaixo.</p>
                            </div>

                            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                                {/* QR Code Imagem */}
                                <div className="bg-white p-2 border rounded-lg shadow-sm">
                                    {paymentData.qrCodeBase64 ? (
                                        <img src={`data:image/png;base64,${paymentData.qrCodeBase64}`} alt="Pix QR Code" className="w-48 h-48" />
                                    ) : (
                                        <div className="w-48 h-48 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                            [QR Code Mock]
                                        </div>
                                    )}
                                </div>

                                {/* Copia e Cola */}
                                <div className="w-full max-w-md space-y-3">
                                    <div className="relative">
                                        <textarea
                                            readOnly
                                            value={paymentData.copyPaste || "00020126360014BR.GOV.BCB.PIX..."}
                                            className="w-full p-3 pr-12 bg-gray-50 border rounded-lg text-xs font-mono text-gray-600 h-24 resize-none focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                        <button
                                            onClick={copyToClipboard}
                                            className="absolute top-2 right-2 p-2 bg-white rounded shadow-sm hover:bg-gray-100 text-blue-600"
                                            title="Copiar Código"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-center text-gray-400 flex items-center justify-center">
                                        <CalendarClock className="w-3 h-3 mr-1" />
                                        Expira em 30 minutos
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 text-center">
                                <button
                                    onClick={() => setPaymentData(null)}
                                    className="text-red-500 hover:text-red-700 text-sm font-medium underline"
                                >
                                    Cancelar Pagamento
                                </button>
                            </div>
                        </div>
                    )}

                    {/* SEÇÃO: MÓDULOS ADICIONAIS PARA CONTRATAR */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-5">
                            <div>
                                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-amber-500" />
                                    Módulos Adicionais Disponíveis
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Potencialize seu atendimento contratando recursos fiscais e de cobrança automática diretamente para a sua empresa.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Card: Emissão de NFS-e */}
                            <div className={`p-5 rounded-xl border-2 transition-all flex flex-col justify-between ${isNfseActive ? 'border-blue-400 bg-blue-50/20 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                <div>
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isNfseActive ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'}`}>
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 text-base">Emissão de NFS-e</h4>
                                                <p className="text-xs text-gray-500">Notas Fiscais de Serviços</p>
                                            </div>
                                        </div>
                                        {isNfseActive ? (
                                            <span className="bg-green-100 text-green-700 font-bold text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                                                <Check className="w-3.5 h-3.5" /> Ativo
                                            </span>
                                        ) : (
                                            <span className="bg-gray-100 text-gray-600 font-bold text-xs px-2.5 py-1 rounded-full">
                                                Disponível
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                        Emita Notas Fiscais de Serviço eletrônicas com alíquotas municipais, cálculo automático de PIS/COFINS/ISS e envio direto do PDF ao cliente via WhatsApp.
                                    </p>

                                    <div className="space-y-1.5 mb-5 text-xs text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                            <span>Emissão simplificada e automatizada</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                            <span>Envio de PDF e XML por WhatsApp</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                            <span>Integração Focus NFe e retenções tributárias</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex items-baseline justify-between mb-3">
                                        <span className="text-xs text-gray-500 uppercase font-semibold">Mensalidade</span>
                                        <div>
                                            <span className="text-xl font-extrabold text-gray-800">R$ {priceNfse.toFixed(2)}</span>
                                            <span className="text-xs text-gray-400 font-medium">/mês</span>
                                        </div>
                                    </div>

                                    {isNfseActive ? (
                                        <button
                                            onClick={() => handleCancelModule('notas_fiscais', 'Emissão de NFS-e')}
                                            disabled={processing}
                                            className="w-full py-2 px-3 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5"
                                        >
                                            <XCircle className="w-4 h-4" /> Desativar Módulo
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleOpenHireModal('notas_fiscais', 'Emissão de NFS-e', priceNfse, 'Emissão automatizada de Notas Fiscais de Serviço eletrônicas integrada ao WhatsApp.')}
                                            disabled={processing}
                                            className="w-full py-2.5 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm transition-colors flex items-center justify-center gap-1.5"
                                        >
                                            <PlusCircle className="w-4 h-4" /> Contratar Módulo
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Card: Emissão de Boletos */}
                            <div className={`p-5 rounded-xl border-2 transition-all flex flex-col justify-between ${isBoletosActive ? 'border-emerald-400 bg-emerald-50/20 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                <div>
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isBoletosActive ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600'}`}>
                                                <DollarSign className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 text-base">Emissão de Boletos</h4>
                                                <p className="text-xs text-gray-500">Gestão Financeira & Cobrança</p>
                                            </div>
                                        </div>
                                        {isBoletosActive ? (
                                            <span className="bg-green-100 text-green-700 font-bold text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                                                <Check className="w-3.5 h-3.5" /> Ativo
                                            </span>
                                        ) : (
                                            <span className="bg-gray-100 text-gray-600 font-bold text-xs px-2.5 py-1 rounded-full">
                                                Disponível
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                        Emita boletos bancários registrados com linha digitável e código Pix Copia e Cola, conciliação e notificações automáticas de cobrança aos seus clientes.
                                    </p>

                                    <div className="space-y-1.5 mb-5 text-xs text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                            <span>Boletos registrados com código de barras e Pix</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                            <span>Contas a pagar, a receber e conciliação</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                            <span>Disparo automático de segunda via no WhatsApp</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex items-baseline justify-between mb-3">
                                        <span className="text-xs text-gray-500 uppercase font-semibold">Mensalidade</span>
                                        <div>
                                            <span className="text-xl font-extrabold text-gray-800">R$ {priceBoletos.toFixed(2)}</span>
                                            <span className="text-xs text-gray-400 font-medium">/mês</span>
                                        </div>
                                    </div>

                                    {isBoletosActive ? (
                                        <button
                                            onClick={() => handleCancelModule('financeiro', 'Emissão de Boletos')}
                                            disabled={processing}
                                            className="w-full py-2 px-3 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5"
                                        >
                                            <XCircle className="w-4 h-4" /> Desativar Módulo
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleOpenHireModal('financeiro', 'Emissão de Boletos', priceBoletos, 'Gestão financeira completa com emissão de boletos bancários e automações de cobrança.')}
                                            disabled={processing}
                                            className="w-full py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-colors flex items-center justify-center gap-1.5"
                                        >
                                            <PlusCircle className="w-4 h-4" /> Contratar Módulo
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dados de Cadastro da Empresa */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-5 flex items-center">
                            <Building className="mr-2 text-gray-400" /> Dados da Empresa / Responsável
                        </h3>

                        {/* CARD RESUMO DADOS DA EMPRESA */}
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xl uppercase shadow-sm border border-blue-200">
                                {company.name ? company.name.substring(0, 2) : 'EM'}
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-gray-800">{company.name}</h4>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold mt-1 ${company.tipo_pessoa === 'PF' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {company.tipo_pessoa === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">Documento ({company.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'})</p>
                                    <p className="text-sm font-medium text-gray-800">{company.tipo_pessoa === 'PF' ? (company.cpf || 'Não informado') : (company.cnpj || 'Não informado')}</p>
                                </div>
                                {company.tipo_pessoa === 'PJ' && (
                                    <div>
                                        <p className="text-xs font-bold text-gray-400 uppercase">Inscrição Estadual</p>
                                        <p className="text-sm font-medium text-gray-800">{company.ie || 'ISENTO'}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">Telefone</p>
                                    <p className="text-sm font-medium text-gray-800 flex items-center">
                                        <Phone className="w-3 h-3 mr-1 text-gray-400" /> {company.phone || 'Não informado'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">E-mail Financeiro</p>
                                    <p className="text-sm font-medium text-gray-800 flex items-center">
                                        <Mail className="w-3 h-3 mr-1 text-gray-400" /> {company.financial_email || company.owner_email}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4 md:border-l md:border-gray-200 md:pl-6">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase flex items-center">
                                        <MapPin className="w-3 h-3 mr-1" /> Endereço
                                    </p>
                                    {company.address?.logradouro ? (
                                        <div className="text-sm font-medium text-gray-800 mt-1 space-y-1">
                                            <p>{company.address.logradouro}, {company.address.numero} {company.address.complemento && `- ${company.address.complemento}`}</p>
                                            <p>{company.address.bairro}</p>
                                            <p>{company.address.cidade} - {company.address.uf}</p>
                                            <p className="text-gray-500">CEP: {company.address.cep}</p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 mt-1 italic">Endereço não cadastrado.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* COLUNA DIREITA: PLANO & RESUMO TOTAL */}
                <div className="space-y-6">
                    <div className="bg-blue-600 p-6 rounded-xl shadow-lg text-white relative overflow-hidden">
                        <div className="relative z-10">
                            <p className="text-blue-200 text-xs font-bold uppercase tracking-wider mb-1">Plano Atual</p>
                            <h2 className="text-3xl font-extrabold mb-1">{company.usersLimit || 3} Usuários</h2>
                            <p className="text-blue-100 text-sm mb-4">Limite de conexões simultâneas.</p>

                            {/* Detalhamento dos custos */}
                            <div className="bg-blue-700/60 p-3 rounded-lg text-xs space-y-1.5 mb-5 border border-blue-500/50">
                                <div className="flex justify-between">
                                    <span className="text-blue-200">Plano Base ({company.usersLimit || 3} usuários):</span>
                                    <span className="font-semibold">R$ {usersTotal.toFixed(2)}</span>
                                </div>
                                {isNfseActive && (
                                    <div className="flex justify-between text-blue-100">
                                        <span>+ Módulo NFS-e:</span>
                                        <span className="font-semibold">R$ {priceNfse.toFixed(2)}</span>
                                    </div>
                                )}
                                {isBoletosActive && (
                                    <div className="flex justify-between text-blue-100">
                                        <span>+ Módulo Boletos:</span>
                                        <span className="font-semibold">R$ {priceBoletos.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-baseline mb-6">
                                <span className="text-4xl font-bold">R$ {currentPrice.toFixed(2)}</span>
                                <span className="text-blue-200 ml-2">/mês total</span>
                            </div>

                            <button
                                onClick={() => setIsUpgradeModalOpen(true)}
                                className="w-full bg-white text-blue-600 py-3 rounded-lg font-bold hover:bg-blue-50 flex items-center justify-center shadow-md transition-colors"
                            >
                                <ArrowUpCircle className="w-5 h-5 mr-2" />
                                Fazer Upgrade de Usuários
                            </button>
                        </div>
                        {/* Decor */}
                        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full"></div>
                    </div>

                    {/* Histórico Simples */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                            <History className="mr-2 text-gray-400" /> Histórico Recente
                        </h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-sm border-b pb-2">
                                <span className="text-gray-600">Mensalidade (Dez)</span>
                                <span className="font-bold text-green-600">Pago</span>
                            </div>
                            <div className="flex justify-between items-center text-sm border-b pb-2">
                                <span className="text-gray-600">Mensalidade (Nov)</span>
                                <span className="font-bold text-green-600">Pago</span>
                            </div>
                            {isOverdue && (
                                <div className="flex justify-between items-center text-sm border-b pb-2 bg-red-50 p-1 rounded">
                                    <span className="text-red-600 font-bold">Mensalidade (Jan)</span>
                                    <span className="font-bold text-red-600">Pendente</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs text-gray-500 flex items-start">
                        <Shield className="w-8 h-8 mr-2 text-gray-400 flex-shrink-0" />
                        <p>
                            Seus dados de pagamento são processados de forma segura por nosso gateway.
                            Nenhuma informação de cartão é armazenada localmente.
                        </p>
                    </div>
                </div>
            </div>

            {/* MODAL DE UPGRADE DE USUÁRIOS */}
            {(() => {
                const currentUsers = company?.usersLimit || 3;
                const diffUsers = Math.max(0, desiredUsers - currentUsers);
                const newMonthlyUsersPrice = 199.90 + ((desiredUsers - 3) * 99.90);
                const proRata = diffUsers > 0 ? calcLocalProRata(company?.next_due_date, diffUsers) : null;
                const confirmLabel = proRata
                    ? `Confirmar e Pagar R$ ${proRata.proRataAmount.toFixed(2)}`
                    : desiredUsers < currentUsers
                        ? 'Confirmar Downgrade'
                        : 'Confirmar';

                return (
                    <Modal
                        isOpen={isUpgradeModalOpen}
                        onClose={() => setIsUpgradeModalOpen(false)}
                        title="Alterar Limite de Usuários"
                        confirmText={confirmLabel}
                        onConfirm={handleUpgradeConfirm}
                        isLoading={processing}
                    >
                        <div className="py-4">
                            <div className="grid grid-cols-1 gap-6 mb-6">
                                <div className="bg-white p-6 rounded-lg border-2 border-blue-100 shadow-sm flex flex-col items-center">
                                    <h3 className="text-lg font-bold text-gray-700 mb-2">Quantos usuários você precisa?</h3>

                                    <div className="flex items-center gap-4 mb-4">
                                        <button
                                            onClick={() => setDesiredUsers(Math.max(3, desiredUsers - 1))}
                                            className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 font-bold text-xl hover:bg-gray-200 transition-colors flex items-center justify-center"
                                        >
                                            -
                                        </button>

                                        <div className="text-center w-32">
                                            <span className="text-4xl font-extrabold text-blue-600">{desiredUsers}</span>
                                            <p className="text-xs uppercase font-bold text-gray-400">Usuários</p>
                                        </div>

                                        <button
                                            onClick={() => setDesiredUsers(desiredUsers + 1)}
                                            className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition-colors flex items-center justify-center"
                                        >
                                            +
                                        </button>
                                    </div>

                                    <div className="w-full h-px bg-gray-100 my-2"></div>

                                    {/* Resumo de preço */}
                                    <div className="w-full mt-3 space-y-2">
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>Valor mensal novo plano de usuários:</span>
                                            <span className="font-semibold">R$ {newMonthlyUsersPrice.toFixed(2)}/mês</span>
                                        </div>

                                        {proRata && (
                                            <>
                                                <div className="flex justify-between text-sm text-gray-600">
                                                    <span>Dias restantes no ciclo:</span>
                                                    <span className="font-semibold">{proRata.daysRemaining} dia(s)</span>
                                                </div>
                                                <div className="w-full h-px bg-gray-100"></div>
                                                <div className="flex justify-between text-base font-bold text-blue-700">
                                                    <span>Cobrança imediata (pró-rata):</span>
                                                    <span>R$ {proRata.proRataAmount.toFixed(2)}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {proRata ? (
                                <div className="bg-blue-50 p-3 rounded text-sm text-blue-800 border border-blue-200">
                                    <p className="font-bold">Como funciona o pró-rata?</p>
                                    <p>Você paga hoje apenas pelo tempo restante até o próximo vencimento ({proRata.daysRemaining} dia(s)).
                                    A partir do próximo mês, a base de usuários da sua fatura será de <strong>R$ {newMonthlyUsersPrice.toFixed(2)}/mês</strong>.</p>
                                </div>
                            ) : (
                                <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-800 border border-yellow-200">
                                    <p className="font-bold">Nota:</p>
                                    <p>Ao diminuir o plano, a mudança será aplicada imediatamente. Não haverá reembolso do período já pago.</p>
                                </div>
                            )}
                        </div>
                    </Modal>
                );
            })()}

            {/* MODAL DE CONTRATAÇÃO DE MÓDULO ADICIONAL */}
            {hiringModule && hireProRata && (
                <Modal
                    isOpen={isHireModalOpen}
                    onClose={() => setIsHireModalOpen(false)}
                    title={`Contratar ${hiringModule.name}`}
                    confirmText={`Confirmar e Contratar (R$ ${hireProRata.proRataAmount.toFixed(2)})`}
                    onConfirm={handleConfirmHireModule}
                    isLoading={processing}
                >
                    <div className="py-3 space-y-4">
                        <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                            <p className="text-sm text-gray-700 mb-2">{hiringModule.description}</p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>Preço regular do módulo:</span>
                                <span className="font-bold text-gray-800 text-sm">R$ {hireProRata.fullMonthly.toFixed(2)}/mês</span>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-sm">
                            <div className="flex justify-between text-gray-600">
                                <span>Dias restantes até o vencimento:</span>
                                <span className="font-semibold text-gray-800">{hireProRata.daysRemaining} dia(s)</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <span>Fator proporcional de cobrança:</span>
                                <span className="font-semibold text-gray-800">{(hireProRata.factor * 100).toFixed(0)}%</span>
                            </div>
                            <div className="w-full h-px bg-gray-200 my-1"></div>
                            <div className="flex justify-between text-base font-bold text-blue-700">
                                <span>Valor a pagar agora (pró-rata):</span>
                                <span>R$ {hireProRata.proRataAmount.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-xs text-amber-800 leading-relaxed">
                            <strong>Aviso:</strong> O módulo será liberado imediatamente no sistema após a confirmação. A partir do seu próximo ciclo de renovação, o valor integral de <strong>R$ {hireProRata.fullMonthly.toFixed(2)}/mês</strong> será incluído na sua fatura mensal.
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AssinaturaScreen;
